import { useCallback } from 'react';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { useTranslation } from '@lyearn/i18n';
import { LinkButton, useSnackbar } from '@lyearn/ui';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import useNavigation from '@/hooks/useNavigation';
import { useShareConnectionItemsMutation } from '@/modules/lyearn-connect/views/LyearnConnectDetails/graphql/mutations/generated/shareItems';
import routes from '@/pages/corporate/routes';
import { SharedItemTypeEnum } from '@/types/schema';

import { WorkspaceMapping } from './types';

const getDetailPageNavigationUrl = (connectionId: string) => ({
  [SharedItemTypeEnum.Session]: routes.LyearnConnectSessionDetails(connectionId),
  [SharedItemTypeEnum.Content]: routes.LyearnConnectContentDetails(connectionId),
  [SharedItemTypeEnum.Collection]: routes.LyearnConnectCollectionDetails(connectionId),
});

const SNACKBAR_MESSAGE_KEY = {
  [SharedItemTypeEnum.Session]: 'LyearnConnect:Snackbar.AcademySessionSharing',
  [SharedItemTypeEnum.Content]: 'LyearnConnect:Snackbar.AcademyContentSharing',
  [SharedItemTypeEnum.Collection]: 'LyearnConnect:Snackbar.AcademyCollectionSharing',
};

const getNavigationUrl = (
  connectionId: string,
  isDetailPageNavigation: boolean,
  type: SharedItemTypeEnum,
) => {
  const detailPageNavigationUrl = getDetailPageNavigationUrl(connectionId)[type];
  return isDetailPageNavigation ? detailPageNavigationUrl : routes.LyearnConnectManager();
};

export default function useShareDialogSubmit() {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const [{ fetching: sharedItemsFetching }, shareItems] = useShareConnectionItemsMutation();
  const navigation = useNavigation();

  const onSubmit = useCallback(
    async ({
      itemId,
      selectedWorkspaceMappings,
      type,
      allowDuplication,
    }: {
      itemId: string;
      type: SharedItemTypeEnum;
      selectedWorkspaceMappings: WorkspaceMapping[];
      allowDuplication: boolean;
    }) => {
      try {
        const selectedWorkspaceIds = selectedWorkspaceMappings.map(
          (sharedToWorkspace) => sharedToWorkspace.workspaceId,
        );
        const shareItemsResponse = await shareItems({
          input: {
            sharedToWorkspaceIds: selectedWorkspaceIds,
            items: [{ itemId, type }],
            allowDuplication,
          },
        });
        if (shareItemsResponse.data?.shareItems?.success) {
          showSnackbar({
            message: t(SNACKBAR_MESSAGE_KEY[type], {
              count: selectedWorkspaceIds.length,
            }),
            action: (
              <LinkButton
                className="flex-shrink-0 py-2 px-4"
                color={'inverse'}
                onClick={() =>
                  navigation.push(
                    getNavigationUrl(
                      selectedWorkspaceMappings[0].connectionId,
                      selectedWorkspaceIds.length === 1,
                      type,
                    ),
                  )
                }>
                {t('Common:Actions.View')}
              </LinkButton>
            ),
          });
        } else {
          ErrorReporter.error(shareItemsResponse.error);
          showDefaultErrorSnackbar();
        }
      } catch (e) {
        ErrorReporter.error(e);
        showDefaultErrorSnackbar();
      }
    },
    [navigation, shareItems, showDefaultErrorSnackbar, showSnackbar, t],
  );

  return { onSubmit, sharedItemsFetching };
}
